import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EVENT_PHOTOS } from './eventPhotos';

type PhotoCarouselProps = {
  onOpen: (src: string) => void;
};

function PhotoCarousel({ onOpen }: PhotoCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const touchStartX = useRef<number | null>(null);

  const total = EVENT_PHOTOS.length;
  const current = EVENT_PHOTOS[index];

  const goTo = useCallback((next: number) => {
    setIndex(((next % total) + total) % total);
  }, [total]);

  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 4500);
    return () => window.clearInterval(id);
  }, [paused, total]);

  useEffect(() => {
    const track = trackRef.current;
    const thumb = thumbRefs.current[index];
    if (!track || !thumb) return;
    const nextLeft = thumb.offsetLeft - (track.clientWidth - thumb.clientWidth) / 2;
    track.scrollTo({ left: Math.max(0, nextLeft), behavior: 'smooth' });
  }, [index]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) goPrev();
    else goNext();
  };

  return (
    <div
      className="photo-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="carousel-stage"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button
          type="button"
          className="carousel-nav carousel-nav-prev"
          onClick={goPrev}
          aria-label="Previous photo"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <button
          type="button"
          className="carousel-main"
          onClick={() => onOpen(current)}
          aria-label={`View event photo ${index + 1} of ${total}`}
        >
          <img
            key={current}
            src={current}
            alt={`Event photo ${index + 1}`}
            className="carousel-main-img"
            loading="eager"
            decoding="async"
          />
          <span className="carousel-main-hint">View full size</span>
        </button>

        <button
          type="button"
          className="carousel-nav carousel-nav-next"
          onClick={goNext}
          aria-label="Next photo"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="carousel-counter" aria-live="polite">
          {index + 1} / {total}
        </div>
      </div>

      <div className="carousel-filmstrip" ref={trackRef} role="listbox" aria-label="Event photo thumbnails">
        {EVENT_PHOTOS.map((src, i) => (
          <button
            key={src}
            type="button"
            role="option"
            aria-selected={i === index}
            className={`carousel-thumb ${i === index ? 'active' : ''}`}
            ref={(el) => { thumbRefs.current[i] = el; }}
            onClick={() => goTo(i)}
          >
            <img
              src={src}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default PhotoCarousel;
